import { useContext, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import getNewsletterSchema from '@app/organisms/EpisodeEditForm/schemas/newsletterSchema';
import { useIntl } from 'react-intl';
import EpisodeEditFormNewsletterEnabledField from '@app/organisms/EpisodeEditForm/EpisodeEditFormNewsletter/EpisodeEditFormNewsletterEnabledField';
import Stack from '@ui/layout/Stack';
import EpisodeEditFormNewsletterTitleField from '@app/organisms/EpisodeEditForm/EpisodeEditFormNewsletter/EpisodeEditFormNewsletterTitleField';
import EpisodeEditFormNewsletterMessageField from '@app/organisms/EpisodeEditForm/EpisodeEditFormNewsletter/EpisodeEditFormNewsletterMessageField';
import NextStepButton from '@app/organisms/EpisodeEditForm/NextStepButton';
import useUpdateEpisodeNewsletterTemplateMutation from '@queries/newsletter/useUpdateEpisodeNewsletterTemplateMutation.hook';
import { useEpisodeBeingEdited } from '@app/organisms/EpisodeEditForm/EpisodeEditFormContext';
import { debounce } from 'lodash';
import styled from 'styled-components';
import EpisodeEditFormNewsletterPreviewButton from '@app/organisms/EpisodeEditForm/EpisodeEditFormNewsletter/EpisodeEditFormNewsletterPreviewButton';
import useEpisodeNewsletterPreviewQuery from '@app/molecules/EpisodeNewsletterForm/useEpisodeNewsletterPreviewQuery.hook';
import { useOverlayTriggerState } from 'react-stately';
import Modal from '@ui/molecules/Modal';
import { EpisodeEditFormNewsletterErrorsCountContext } from '@app/organisms/EpisodeEditForm/contexts/EpisodeEditFormNewsletterErrorsCountContext';

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    column-gap: 0.5rem;
    align-items: center;

    ${(props) => props.theme.mediaQueries.tabletAndUp} {
        justify-content: space-between;
    }
`;

const NewsletterPreview = styled.div`
    height: 100%;
    overflow-y: auto;
`;

// TODO: Missing quota warning and quota update ?
const EpisodeEditFormNewsletterForm = ({ template }) => {
    const intl = useIntl();
    const { watch: watchEpisodeForm } = useFormContext();
    const episode = useEpisodeBeingEdited();
    const episodeName = watchEpisodeForm('content.name');
    const schema = getNewsletterSchema(intl);
    const previewModalState = useOverlayTriggerState({});
    const episodeNewsletterPreviewQuery = useEpisodeNewsletterPreviewQuery(episode.id, episodeName);
    const updateEpisodeNewsletterTemplateMutation = useUpdateEpisodeNewsletterTemplateMutation();
    const { setNewsletterErrorsCount } = useContext(EpisodeEditFormNewsletterErrorsCountContext);

    const methods = useForm({
        mode: 'onBlur',
        values: {
            activated: template.activated,
            title: template.title,
            messageTpl: template.messageTpl,
        },
        resetOptions: {
            keepDirtyValues: true,
            keepErrors: true,
        },
        resolver: yupResolver(schema),
    });

    const {
        watch,
        handleSubmit,
        formState: { errors },
    } = methods;

    // Pull form errors count up to the accordion
    useEffect(() => {
        setNewsletterErrorsCount(Object.keys(errors).length);
    }, [errors]);

    // Auto-submit form when an input value change
    useEffect(() => {
        const subscription = watch(() => {
            handleSubmit(onSubmit)();
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Save episode newsletter template
    const onSubmit = debounce((data) => {
        updateEpisodeNewsletterTemplateMutation.mutate({
            episodeId: episode.id,
            template: data,
        });
    }, 500);

    // Save episode newsletter template and fetch preview of the newsletter
    const onPreview = async () => {
        await handleSubmit(onSubmit)();
        await episodeNewsletterPreviewQuery.refetch();
        previewModalState.open();
    };

    const isNewsletterEnabled = watch('activated');

    return (
        <FormProvider {...methods}>
            <Stack $gap="2rem">
                <EpisodeEditFormNewsletterEnabledField />
                {isNewsletterEnabled ? (
                    <>
                        <EpisodeEditFormNewsletterTitleField />
                        <EpisodeEditFormNewsletterMessageField />
                        <Actions>
                            <EpisodeEditFormNewsletterPreviewButton onPress={onPreview} />
                            <NextStepButton />
                        </Actions>
                    </>
                ) : (
                    <NextStepButton />
                )}
            </Stack>
            {previewModalState.isOpen && (
                <Modal
                    size="medium"
                    isOpen={previewModalState.isOpen}
                    onClose={previewModalState.close}
                >
                    <NewsletterPreview
                        dangerouslySetInnerHTML={{ __html: episodeNewsletterPreviewQuery.data }}
                    />
                </Modal>
            )}
        </FormProvider>
    );
};

export default EpisodeEditFormNewsletterForm;
